import { motion, AnimatePresence } from 'framer-motion'
import SEO from '../components/SEO'
import { Send, CheckCircle, Star } from 'lucide-react'
import Footer from '../components/Footer'
import { useState } from 'react'

const ratingOptions = [
  { value: 5, label: 'Excellent' },
  { value: 4, label: 'Good' },
  { value: 3, label: 'Average' },
  { value: 2, label: 'Below Average' },
  { value: 1, label: 'Poor' },
]

interface FieldErrors {
  name?: string
  email?: string
  message?: string
}

export default function Feedback() {
  const [sent, setSent] = useState(false)
  const [rating, setRating] = useState(5)
  const [hoverRating, setHoverRating] = useState(0)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  function validate(form: HTMLFormElement): FieldErrors {
    const data = new FormData(form)
    const errs: FieldErrors = {}
    if (!data.get('name')?.toString().trim()) errs.name = 'Name is required'
    const email = data.get('email')?.toString().trim() || ''
    if (!email) errs.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Enter a valid email'
    if (!data.get('message')?.toString().trim()) errs.message = 'Feedback is required'
    return errs
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const field = e.target.name
    setTouched(prev => ({ ...prev, [field]: true }))
    const form = e.target.closest('form')
    if (form) {
      const errs = validate(form)
      setErrors(prev => ({ ...prev, [field]: errs[field as keyof FieldErrors] }))
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const field = e.target.name as keyof FieldErrors
    if (touched[field] && errors[field]) {
      const form = e.target.closest('form')
      if (form) {
        const errs = validate(form)
        setErrors(prev => ({ ...prev, [field]: errs[field] }))
      }
    }
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const errs = validate(form)
    setErrors(errs)
    setTouched({ name: true, email: true, message: true })

    const hasErrors = Object.keys(errs).length > 0
    if (hasErrors) {
      // Focus the first invalid field
      const firstErrorField = Object.keys(errs)[0]
      const el = form.querySelector<HTMLElement>(`[name="${firstErrorField}"]`)
      el?.focus()
      return
    }

    const data = new FormData(form)
    const name = data.get('name')?.toString().trim() || 'Anonymous'
    const project = data.get('project')?.toString().trim() || ''
    const email = data.get('email')?.toString().trim() || ''
    const message = data.get('message')?.toString().trim() || ''

    const body = `Feedback from ${name}\nEmail: ${email}\nProject: ${project}\nRating: ${rating}/5\n\n${message}`
    window.open(
      `https://github.com/xvertere-org/Solarch/issues/new?title=Feedback: ${project}&body=${encodeURIComponent(body)}`,
      '_blank'
    )
    setSent(true)
  }

  const displayRating = hoverRating || rating
  const ratingLabel = ratingOptions.find(r => r.value === displayRating)?.label || ''

  function fieldClass(field: keyof FieldErrors) {
    const base = "w-full rounded-lg border bg-[#0a0603] px-4 py-3 text-sm text-[#fdf3ec] placeholder:text-[#8b6d5b] outline-none transition-all duration-200 focus:shadow-[0_0_0_3px_rgba(255,90,31,0.1)] focus-visible:outline-none"
    const hasError = touched[field] && errors[field]
    return `${base} ${hasError ? 'border-red-500/60 focus:border-red-500/60' : 'border-[#3a2214] focus:border-primary/60'}`
  }

  return (
    <>
      <SEO 
        title="Feedback — Solarch"
        description="Share your feedback or report about building with Solarch."
        canonical="/feedback"
        noindex={true}
      />

      <div className="min-h-screen bg-theme-body pt-14">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute right-1/4 top-0 h-[400px] w-[400px] rounded-full bg-primary/5 blur-[120px]" />
            <div className="absolute left-1/4 bottom-0 h-[300px] w-[300px] rounded-full bg-primary/3 blur-[100px]" />
          </div>

          <div className="relative mx-auto max-w-3xl px-4 pt-20 pb-12 sm:px-6 lg:px-8 lg:pt-28 lg:pb-16 text-center">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
            >
              <h1 className="font-heading text-4xl font-bold tracking-tight text-[#fdf3ec] sm:text-5xl">
                Share your <span className="text-primary">feedback</span>
              </h1>
              <p className="mx-auto mt-4 max-w-lg text-lg text-[#c9a894] leading-relaxed">
                Built something with Solarch? Tell us about your experience, report a bug, or suggest an improvement.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Form */}
        <section className="mx-auto max-w-2xl px-4 pb-24 sm:px-6 lg:px-8">
          {sent ? (
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
              className="rounded-2xl border border-[#3a2214] bg-[#150d08] p-12 text-center"
            >
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10 border border-green-500/20">
                <CheckCircle className="h-7 w-7 text-green-500" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#fdf3ec] mb-2">Thank you!</h2>
              <p className="text-[#c9a894] text-base max-w-sm mx-auto">
                Your feedback has been submitted. We appreciate your help making Solarch better.
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.32, 0.72, 0, 1] }}
              onSubmit={handleSubmit}
              noValidate
              className="rounded-2xl border border-[#3a2214] bg-[#150d08] p-8 sm:p-10 space-y-8"
            >
              {/* Name & Email — side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="feedback-name" className="block text-sm font-medium text-[#fdf3ec] mb-2">
                    Your Name <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    id="feedback-name"
                    name="name"
                    placeholder="Jane Doe"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={fieldClass('name')}
                  />
                  <AnimatePresence>
                    {touched.name && errors.name && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="mt-1.5 text-xs text-red-400"
                      >
                        {errors.name}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
                <div>
                  <label htmlFor="feedback-email" className="block text-sm font-medium text-[#fdf3ec] mb-2">
                    Email <span className="text-primary">*</span>
                  </label>
                  <input
                    type="email"
                    id="feedback-email"
                    name="email"
                    placeholder="jane@example.com"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={fieldClass('email')}
                  />
                  <AnimatePresence>
                    {touched.email && errors.email && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="mt-1.5 text-xs text-red-400"
                      >
                        {errors.email}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Project Name */}
              <div>
                <label htmlFor="feedback-project" className="block text-sm font-medium text-[#fdf3ec] mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  id="feedback-project"
                  name="project"
                  placeholder="My Awesome App"
                  className="w-full rounded-lg border border-[#3a2214] bg-[#0a0603] px-4 py-3 text-sm text-[#fdf3ec] placeholder:text-[#8b6d5b] outline-none transition-all duration-200 focus:border-primary/60 focus:shadow-[0_0_0_3px_rgba(255,90,31,0.1)] focus-visible:outline-none"
                />
              </div>

              {/* Star Rating */}
              <div>
                <label className="block text-sm font-medium text-[#fdf3ec] mb-3">
                  How would you rate your experience?
                </label>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="group p-1 transition-transform duration-150 hover:scale-110 active:scale-95"
                        aria-label={`Rate ${star} out of 5`}
                      >
                        <Star
                          className={`h-7 w-7 transition-colors duration-150 ${
                            star <= displayRating
                              ? 'text-primary fill-primary'
                              : 'text-[#3a2214] fill-transparent'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <span className="text-sm text-[#c9a894] font-medium min-w-[100px]">
                    {displayRating}/5 — {ratingLabel}
                  </span>
                </div>
                <input type="hidden" name="rating" value={rating} />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="feedback-message" className="block text-sm font-medium text-[#fdf3ec] mb-2">
                  Your Feedback <span className="text-primary">*</span>
                </label>
                <textarea
                  id="feedback-message"
                  name="message"
                  rows={5}
                  placeholder="Tell us what you built, what worked well, or what could be improved..."
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className={`${fieldClass('message')} resize-y min-h-[120px]`}
                />
                <AnimatePresence>
                  {touched.message && errors.message && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="mt-1.5 text-xs text-red-400"
                    >
                      {errors.message}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Submit */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <button
                  type="submit"
                  className="group inline-flex items-center gap-2.5 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
                >
                  <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  Submit Feedback
                </button>
                <p className="text-xs text-[#8b6d5b] leading-relaxed">
                  Opens as a GitHub issue. Account required.
                </p>
              </div>
            </motion.form>
          )}
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Footer />
        </div>
      </div>
    </>
  )
}
